import type { DependencyEdgeType } from '../types';

/**
 * PMI-style anchors on task bars — dependency lines leave/arrive horizontally from the rail
 * (Finish = right-facing, Start = left-facing), matching common scheduling / diagram-tool conventions.
 */

const TENSION_MIN = 18;
const TENSION_MAX = 112;
/** Same-column vertical run (timeline strip gutters, pillar drops): straight spine reads clearer than a Bézier spine. */
const VERTICAL_PURGE_ABS_DX = 0.95;

/** Unit tangents along the outgoing pred edge and incoming succ edge (both horizontal for our Gantt anchors). */
export function ganttDependencyTangents(
  edgeType: DependencyEdgeType,
): { outX: number; outY: number; inX: number; inY: number } {
  switch (edgeType) {
    case 'FS':
      return { outX: 1, outY: 0, inX: 1, inY: 0 };
    case 'FF':
      return { outX: 1, outY: 0, inX: -1, inY: 0 };
    case 'SS':
      return { outX: -1, outY: 0, inX: 1, inY: 0 };
    case 'SF':
      return { outX: -1, outY: 0, inX: -1, inY: 0 };
    default:
      return { outX: 1, outY: 0, inX: 1, inY: 0 };
  }
}

function rnd(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Single connector primitive for FS / SS / FF / SF links.
 *
 * Uses a cardinal cubic Bézier (horizontal control arms) derived from chord span — stable across zoom presets
 * without “dramatic curve” bifurcation. Purely vertical hops use a linear path for a clean strip column spine.
 *
 * References: ubiquitous “smooth orthogonal-ish” spline in UI tools — horizontal tangents at box edges;
 * curvature from endpoint separation (`|dx|, |dy|`).
 */
export function ganttDependencyConnectorPath(
  x0: number,
  y0: number,
  x3: number,
  y3: number,
  edgeType: DependencyEdgeType,
): string {
  const dx = x3 - x0;
  const dy = y3 - y0;
  const absDx = Math.abs(dx);
  const absDy = Math.abs(dy);

  if (absDx < 1e-5 && absDy < 1e-5) {
    return `M ${rnd(x0)} ${rnd(y0)}`;
  }

  if (absDx < VERTICAL_PURGE_ABS_DX && absDy > VERTICAL_PURGE_ABS_DX) {
    return `M ${rnd(x0)} ${rnd(y0)} L ${rnd(x3)} ${rnd(y3)}`;
  }

  const { outX, outY, inX, inY } = ganttDependencyTangents(edgeType);

  const axial = absDx >= absDy ? absDx : absDy;
  const cross = absDx >= absDy ? absDy : absDx;

  let tension = Math.max(TENSION_MIN, Math.min(TENSION_MAX, 0.43 * axial + 0.26 * cross));

  if (absDy >= absDx * 1.12 && absDy > 28) {
    tension = Math.max(tension, Math.min(TENSION_MAX, 24 + absDy * 0.17));
  }

  /** Same-calendar-row / near-row: tame lateral flare. */
  if (absDy < 12) {
    tension = Math.max(12, Math.min(TENSION_MAX * 0.55, tension * 0.62, Math.max(absDx * 0.45, 16)));
  }

  const x1 = x0 + outX * tension;
  const y1 = y0 + outY * tension;
  const x2 = x3 - inX * tension;
  const y2 = y3 - inY * tension;

  return `M ${rnd(x0)} ${rnd(y0)} C ${rnd(x1)} ${rnd(y1)} ${rnd(x2)} ${rnd(y2)} ${rnd(x3)} ${rnd(y3)}`;
}
